import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";

import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Typography,
  Upload,
  Image,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Tag,
  Empty,
} from "antd";

import {
  EnvironmentOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined,
  CameraOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import destinationService from "../services/destinationService";



const { Option } = Select;
const { Title, Text } = Typography;

const getDestinationImageUrl = (image = "") => {
  if (!image) return "";

  const value = String(image).trim();

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalizedPath = value
    .replace(/^\/+/, "")
    .replace(/^api\/uploads\//i, "uploads/")
    .replace(/^uploads\//i, "uploads/");

  return `${window.location.origin}/${normalizedPath}`;
};

const DestinationManagement = () => {
  const { user } = useAuth();

  // =====================================================
  // STATE
  // =====================================================

  const [destinations, setDestinations] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [modalVisible, setModalVisible] =
    useState(false);

  const [
    editingDestination,
    setEditingDestination,
  ] = useState(null);

  const [imageUrl, setImageUrl] =
    useState("");

  const [uploading, setUploading] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [form] = Form.useForm();

  // =====================================================
  // CURRENCIES
  // =====================================================

  const currencies = [
    {
      code: "INR",
      name: "Indian Rupee",
      symbol: "₹",
    },
    {
      code: "USD",
      name: "US Dollar",
      symbol: "$",
    },
    {
      code: "EUR",
      name: "Euro",
      symbol: "€",
    },
    {
      code: "GBP",
      name: "British Pound",
      symbol: "£",
    },
    {
      code: "AUD",
      name: "Australian Dollar",
      symbol: "A$",
    },
    {
      code: "CAD",
      name: "Canadian Dollar",
      symbol: "C$",
    },
    {
      code: "JPY",
      name: "Japanese Yen",
      symbol: "¥",
    },
    {
      code: "SGD",
      name: "Singapore Dollar",
      symbol: "S$",
    },
    {
      code: "AED",
      name: "UAE Dirham",
      symbol: "د.إ",
    },
    {
      code: "THB",
      name: "Thai Baht",
      symbol: "฿",
    },
  ];

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    fetchDestinations();
  }, []);

  // =====================================================
  // RESET MODAL STATE
  // =====================================================

  const resetModalState = () => {

    setModalVisible(false);

    setEditingDestination(null);

    setImageUrl("");

    form.resetFields();
  };

  // =====================================================
  // FETCH DESTINATIONS
  // =====================================================

  const fetchDestinations =
    async () => {

      try {

        setLoading(true);

        const response =
          await destinationService.getAll();

setDestinations(
  Array.isArray(response?.data)
    ? response.data
    : []
);

      } catch (error) {

        console.error(
          "Error fetching destinations:",
          error
        );

        message.error(
          "Failed to fetch destinations"
        );

      } finally {

        setLoading(false);
      }
    };

  // =====================================================
  // FILTERED DESTINATIONS
  // =====================================================

  const filteredDestinations =
    useMemo(() => {

      return destinations.filter(
        (destination) =>
          destination?.name
            ?.toLowerCase()
            .includes(
              (search || "").toLowerCase()
            )
      );
    }, [destinations, search]);

  // =====================================================
  // ADD DESTINATION
  // =====================================================

  const handleAddDestination =
    () => {

      setEditingDestination(
        null
      );

      setImageUrl("");

      form.resetFields();

      setModalVisible(true);
    };

  // =====================================================
  // EDIT DESTINATION
  // =====================================================

  const handleEditDestination =
    (destination) => {

      setEditingDestination(
        destination
      );

      setImageUrl(
        destination.image || ""
      );

      form.setFieldsValue({
        name: destination.name,
        currency:
          destination.currency,
      });

      setModalVisible(true);
    };

  // =====================================================
  // DELETE DESTINATION
  // =====================================================

  const handleDeleteDestination =
    async (id) => {

      try {

        await destinationService.delete(
          id
        );

        message.success(
          "Destination deleted successfully"
        );

        fetchDestinations();

      } catch (error) {

        console.error(
          "Error deleting destination:",
          error
        );

        message.error(
          "Failed to delete destination"
        );
      }
    };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit =
    async (values) => {

      try {

        const payload = {
          name: values.name,
          currency:
            values.currency,
          image: imageUrl,
        };

        if (
          editingDestination
        ) {

          await destinationService.update(
            editingDestination._id,
            payload
          );

          message.success(
            "Destination updated successfully"
          );

        } else {

          await destinationService.create(
            payload
          );

          message.success(
            "Destination created successfully"
          );
        }

        resetModalState();

        fetchDestinations();

      } catch (error) {

        console.error(
          "Error saving destination:",
          error
        );

        message.error(
          error?.response?.data
            ?.message ||
            "Failed to save destination"
        );
      }
    };

  // =====================================================
  // IMAGE UPLOAD
  // =====================================================

const handleImageUpload =
  async (file) => {

    try {

      const isImage =
        file.type.startsWith(
          "image/"
        );

      if (!isImage) {

        message.error(
          "Only image files allowed"
        );

        return Upload.LIST_IGNORE;
      }

      const isLt5M =
        file.size / 1024 / 1024 < 5;

      if (!isLt5M) {

        message.error(
          "Image must be smaller than 5MB"
        );

        return Upload.LIST_IGNORE;
      }

      setUploading(true);

      const response =
        await destinationService.uploadImage(
          file
        );

const uploadedImage =
  response?.imageUrl ||
  response?.data?.imageUrl;

if (!uploadedImage) {
  throw new Error(
    "Image upload failed"
  );
}

setImageUrl(
  uploadedImage
);

      message.success(
        "Image uploaded successfully"
      );

      return false;

    } catch (error) {

      console.error(
        "Upload error:",
        error
      );

      message.error(
        error?.response?.data
          ?.message ||
          "Failed to upload image"
      );

      return Upload.LIST_IGNORE;

    } finally {

      setUploading(false);
    }
  };

  // =====================================================
  // TABLE COLUMNS
  // =====================================================

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 110,

      render: (
        image,
        record
      ) => (

        <div
          style={{
            width: 70,
            height: 50,
            overflow: "hidden",
            borderRadius: 10,
          }}
        >

{image ? (

  <Image
    src={getDestinationImageUrl(image)}
    alt={record.name}
    style={{
      width: "100%",
      height: "100%",
      objectFit:
        "cover",
    }}
    preview={{
      mask:
        <EyeOutlined />,
    }}
  />

) : (

            <div
              style={{
                width: "100%",
                height: "100%",
                background:
                  "#f5f5f5",
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                border:
                  "1px dashed #d9d9d9",
              }}
            >

              <CameraOutlined
                style={{
                  color:
                    "#bfbfbf",
                }}
              />

            </div>
          )}

        </div>
      ),
    },

    {
      title:
        "Destination Name",

      dataIndex: "name",

      key: "name",

      render: (name) => (

        <Space>

          <EnvironmentOutlined
            style={{
              color:
                "#1677ff",
            }}
          />

          <Text strong>
            {name}
          </Text>

        </Space>
      ),
    },

    {
      title: "Currency",

      dataIndex: "currency",

      key: "currency",

      render: (currency) => {

        const currencyInfo =
          currencies.find(
            (c) =>
              c.code === currency
          );

        return (

          <Tag color="blue">

            {currencyInfo?.symbol}
            {" "}
            {currency}

          </Tag>
        );
      },
    },

    {
      title:
        "Created By",

      dataIndex: "createdBy",

      key: "createdBy",

      render: (createdBy) =>
        createdBy?.name ||
        user?.name ||
        "System",
    },

    {
      title:
        "Created Date",

      dataIndex: "createdAt",

      key: "createdAt",

      render: (date) =>
        new Date(
          date
        ).toLocaleDateString(),
    },

    {
      title: "Actions",

      key: "actions",

      width: 140,

      render: (_, record) => (

        <Space>

          <Tooltip title="Edit">

            <Button
              type="primary"
              size="small"
              icon={
                <EditOutlined />
              }
              onClick={() =>
                handleEditDestination(
                  record
                )
              }
            />

          </Tooltip>

          <Tooltip title="Delete">

            <Popconfirm
              title="Delete destination?"
              onConfirm={() =>
                handleDeleteDestination(
                  record._id
                )
              }
            >

              <Button
                danger
                size="small"
                icon={
                  <DeleteOutlined />
                }
              />

            </Popconfirm>

          </Tooltip>

        </Space>
      ),
    },
  ];

  // =====================================================
  // UI
  // =====================================================

  return (

    <div
      style={{
        padding: 24,
      }}
    >

      <Card
        style={{
          borderRadius: 20,
        }}
      >

        {/* =====================================================
            HEADER
        ===================================================== */}

        <Row
          justify="space-between"
          align="middle"
          style={{
            marginBottom: 24,
          }}
        >

          <Col>

            <Title
              level={2}
              style={{
                margin: 0,
              }}
            >

              <EnvironmentOutlined
                style={{
                  marginRight: 10,
                  color:
                    "#1677ff",
                }}
              />

              Destination Management

            </Title>

            <Text type="secondary">
              Manage travel destinations and currencies
            </Text>

          </Col>

          <Col>

            <Button
              type="primary"
              size="large"
              icon={
                <PlusOutlined />
              }
              onClick={
                handleAddDestination
              }
            >
              New Destination
            </Button>

          </Col>

        </Row>

        {/* =====================================================
            SEARCH
        ===================================================== */}

        <Row
          style={{
            marginBottom: 20,
          }}
        >

          <Col span={8}>

            <Input
              placeholder="Search destinations..."
              prefix={
                <SearchOutlined />
              }
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

          </Col>

        </Row>

        {/* =====================================================
            TABLE
        ===================================================== */}

        <Table
          columns={columns}
          dataSource={
            filteredDestinations
          }
          rowKey="_id"
          loading={loading}
          locale={{
            emptyText:
              <Empty description="No destinations found" />,
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />

      </Card>

      {/* =====================================================
          MODAL
      ===================================================== */}

      <Modal
        open={modalVisible}
        onCancel={
          resetModalState
        }
        footer={null}
        width={700}
        destroyOnClose
        title={
          <Space>

            <EnvironmentOutlined />

            {editingDestination
              ? "Edit Destination"
              : "Add Destination"}

          </Space>
        }
      >

        <Form
          form={form}
          layout="vertical"
          onFinish={
            handleSubmit
          }
          style={{
            marginTop: 24,
          }}
        >

          {/* NAME */}

          <Form.Item
            name="name"
            label="Destination Name"
            rules={[
              {
                required: true,
                message:
                  "Please enter destination name",
              },
            ]}
          >

            <Input
              size="large"
              prefix={
                <EnvironmentOutlined />
              }
              placeholder="Enter destination name"
            />

          </Form.Item>

          {/* CURRENCY */}

          <Form.Item
            name="currency"
            label="Currency"
            rules={[
              {
                required: true,
                message:
                  "Please select currency",
              },
            ]}
          >

            <Select
              showSearch
              size="large"
              placeholder="Select currency"

              filterOption={(
                input,
                option
              ) =>
                String(
                  option?.value || ""
                )
                  .toLowerCase()
                  .includes(
                    input.toLowerCase()
                  )
              }
            >

              {currencies.map(
                (currency) => (

                  <Option
                    key={
                      currency.code
                    }
                    value={
                      currency.code
                    }
                  >

                    {currency.symbol}
                    {" "}
                    {currency.code}
                    {" "}
                    -{" "}
                    {currency.name}

                  </Option>
                )
              )}

            </Select>

          </Form.Item>

          {/* IMAGE */}

          <Form.Item label="Destination Image">

            <div
              style={{
                display: "flex",
                gap: 20,
              }}
            >

              <Upload
                name="image"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={
                  handleImageUpload
                }
                accept="image/*"
              >

                {imageUrl ? (

                  <Image
                    src={`${window.location.origin}${imageUrl}`}
                    alt="destination"
                    preview={false}
                    style={{
                      width: 120,
                      height: 100,
                      objectFit:
                        "cover",
                    }}
                  />

                ) : (

                  <div>

                    {uploading ? (
                      <div>
                        Uploading...
                      </div>
                    ) : (
                      <>

                        <UploadOutlined
                          style={{
                            fontSize: 24,
                          }}
                        />

                        <div
                          style={{
                            marginTop: 8,
                          }}
                        >
                          Upload
                        </div>

                      </>
                    )}

                  </div>
                )}

              </Upload>

              <div
                style={{
                  flex: 1,
                }}
              >

                <Text type="secondary">

Upload destination cover image.

<br />

<Text type="secondary">
  Recommended:
  1200×750px,
  WebP/JPG,
  under 300KB
</Text>

                </Text>

                {imageUrl && (

                  <div
                    style={{
                      marginTop: 10,
                    }}
                  >

                    <Button
                      size="small"
                      onClick={() =>
                        setImageUrl("")
                      }
                    >
                      Remove Image
                    </Button>

                  </div>
                )}

              </div>

            </div>

          </Form.Item>

          {/* FOOTER */}

          <div
            style={{
              textAlign: "right",
              marginTop: 30,
            }}
          >

            <Space>

              <Button
                onClick={
                  resetModalState
                }
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={uploading}
              >

                {editingDestination
                  ? "Update Destination"
                  : "Create Destination"}

              </Button>

            </Space>

          </div>

        </Form>

      </Modal>

    </div>
  );
};

export default DestinationManagement;
